import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  query, where, orderBy, onSnapshot, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';

// ======== CONSTANTES DE ROLES ========
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  MASTER: 'master',
  GESTOR: 'gestor',
  USUARIO: 'usuario',
};

export const ROLE_LABELS = {
  super_admin: 'Super Admin',
  master: 'Master',
  gestor: 'Gestor',
  usuario: 'Usuário',
};

export const ROLE_COLORS = {
  super_admin: { bg: 'rgba(239,68,68,0.1)', color: '#DC2626' },
  master: { bg: 'rgba(139,92,246,0.1)', color: '#8B5CF6' },
  gestor: { bg: 'rgba(59,130,246,0.1)', color: '#3B82F6' },
  usuario: { bg: 'rgba(100,116,139,0.1)', color: '#64748B' },
};

// ======== COLLECTIONS ========
const USERS_COL = 'users';
const TEAMS_COL = 'teams';

// ======== SUPER ADMIN EMAILS ========
// Emails que recebem automaticamente o papel de super_admin no primeiro login.
// Adicione aqui os emails dos administradores da plataforma.
const SUPER_ADMIN_EMAILS = [
  'david.oliveira@monetare.com.br',
];

// ======== ESTRUTURA DO USER ========
// users/{uid}: {
//   uid, email, displayName, role, masterId,
//   gestorId (se usuario), teamId (se pertence a equipe),
//   status, criadoEm, atualizadoEm
// }

// ======== INICIALIZAR MASTER ========
// Chamado no primeiro login quando não existe registro pendente
export const initializeMaster = async (user) => {
  const ref = doc(db, USERS_COL, user.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    // Verifica se o email é de super admin
    const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(
      (user.email || '').toLowerCase().trim()
    );

    const role = isSuperAdmin ? ROLES.SUPER_ADMIN : ROLES.MASTER;

    await setDoc(ref, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || '',
      role,
      masterId: isSuperAdmin ? null : user.uid,
      gestorId: null,
      teamId: null,
      status: 'active',
      criadoEm: serverTimestamp(),
      atualizadoEm: serverTimestamp(),
    });
    return { uid: user.uid, role, masterId: isSuperAdmin ? null : user.uid };
  }
  return { uid: snap.id, ...snap.data() };
};

// ======== BUSCAR PERFIL DO USUÁRIO ========
export const getUserProfile = async (uid) => {
  const snap = await getDoc(doc(db, USERS_COL, uid));
  if (!snap.exists()) return null;
  return { uid: snap.id, ...snap.data() };
};

// ======== LISTENER DO PERFIL ========
export const subscribeUserProfile = (uid, callback) => {
  return onSnapshot(doc(db, USERS_COL, uid), (snap) => {
    if (snap.exists()) callback({ uid: snap.id, ...snap.data() });
    else callback(null);
  });
};

// ======== CRIAR USUÁRIO (convite) ========
// Master cria gestor ou usuario
// Gestor cria usuario dentro da sua hierarquia
// Super Admin pode criar qualquer role
export const createUserInvite = async (creatorProfile, data) => {
  const { email, displayName, role, teamId } = data;

  // Validar permissões
  if (creatorProfile.role === ROLES.USUARIO) {
    throw new Error('Usuários não podem criar outros usuários.');
  }
  if (creatorProfile.role === ROLES.GESTOR && role === ROLES.MASTER) {
    throw new Error('Gestores não podem criar Masters.');
  }
  if (creatorProfile.role === ROLES.GESTOR && role === ROLES.GESTOR) {
    throw new Error('Gestores não podem criar outros Gestores.');
  }
  if (creatorProfile.role === ROLES.MASTER && role === ROLES.SUPER_ADMIN) {
    throw new Error('Masters não podem criar Super Admins.');
  }
  if (creatorProfile.role === ROLES.GESTOR && role === ROLES.SUPER_ADMIN) {
    throw new Error('Gestores não podem criar Super Admins.');
  }

  // Verificar se email já existe
  const q = query(collection(db, USERS_COL), where('email', '==', email));
  const existing = await getDocs(q);
  if (!existing.empty) {
    throw new Error('Este e-mail já está cadastrado no sistema.');
  }

  // Determinar masterId e gestorId
  let masterId = null;
  let gestorId = null;

  if (role !== ROLES.SUPER_ADMIN) {
    if (creatorProfile.role === ROLES.SUPER_ADMIN) {
      // Super Admin criando master — masterId será preenchido depois
      masterId = role === ROLES.MASTER ? null : (data.masterId || null);
    } else {
      masterId = creatorProfile.role === ROLES.MASTER
        ? creatorProfile.uid
        : creatorProfile.masterId;
    }

    gestorId = role === ROLES.USUARIO
      ? (creatorProfile.role === ROLES.GESTOR ? creatorProfile.uid : data.gestorId || null)
      : null;
  }

  // Criar registro pendente
  const userRef = doc(collection(db, USERS_COL));
  await setDoc(userRef, {
    uid: null,
    email: email.toLowerCase().trim(),
    displayName: displayName || '',
    role,
    masterId,
    gestorId,
    teamId: teamId || null,
    status: 'pending',
    criadoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });

  return userRef.id;
};

// ======== VINCULAR UID AO PRIMEIRO LOGIN ========
export const linkUserOnFirstLogin = async (user) => {
  // Buscar registro pendente pelo email
  const q = query(
    collection(db, USERS_COL),
    where('email', '==', user.email.toLowerCase()),
    where('status', '==', 'pending')
  );
  const snap = await getDocs(q);

  if (!snap.empty) {
    const pendingDoc = snap.docs[0];
    const pendingData = pendingDoc.data();

    // Atualizar com uid real
    const updates = {
      uid: user.uid,
      displayName: user.displayName || pendingData.displayName,
      status: 'active',
      atualizadoEm: serverTimestamp(),
    };

    // Se for master, o masterId é o próprio uid
    if (pendingData.role === ROLES.MASTER && !pendingData.masterId) {
      updates.masterId = user.uid;
    }

    await updateDoc(doc(db, USERS_COL, pendingDoc.id), updates);

    // Criar doc com uid como ID para acesso rápido
    const profileData = { ...pendingData, ...updates };
    delete profileData.criadoEm;
    await setDoc(doc(db, USERS_COL, user.uid), {
      ...profileData,
      criadoEm: serverTimestamp(),
      atualizadoEm: serverTimestamp(),
    });

    // Remover doc antigo
    if (pendingDoc.id !== user.uid) {
      await deleteDoc(doc(db, USERS_COL, pendingDoc.id));
    }

    return getUserProfile(user.uid);
  }

  // Se não existe registro pendente, é um novo master (ou super admin)
  return initializeMaster(user);
};

// ======== LISTAR USUÁRIOS ========
// Super Admin vê todos
// Master vê todos os seus usuários
// Gestor vê apenas os seus usuários
export const subscribeUsers = (profile, callback) => {
  let q;

  if (profile.role === ROLES.SUPER_ADMIN) {
    // Super admin vê todos os usuários da plataforma
    q = query(
      collection(db, USERS_COL),
      orderBy('criadoEm', 'desc')
    );
  } else if (profile.role === ROLES.MASTER) {
    q = query(
      collection(db, USERS_COL),
      where('masterId', '==', profile.uid),
      orderBy('criadoEm', 'desc')
    );
  } else if (profile.role === ROLES.GESTOR) {
    q = query(
      collection(db, USERS_COL),
      where('gestorId', '==', profile.uid),
      orderBy('criadoEm', 'desc')
    );
  } else {
    callback([]);
    return () => {};
  }

  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

// ======== ATUALIZAR ROLE ========
export const updateUserRole = async (creatorProfile, targetUserId, newRole) => {
  if (creatorProfile.role === ROLES.USUARIO) {
    throw new Error('Sem permissão.');
  }
  if (creatorProfile.role === ROLES.GESTOR && newRole !== ROLES.USUARIO) {
    throw new Error('Gestores só podem definir o papel Usuário.');
  }
  if (creatorProfile.role === ROLES.MASTER && newRole === ROLES.SUPER_ADMIN) {
    throw new Error('Masters não podem promover a Super Admin.');
  }
  // Super Admin pode alterar qualquer role

  await updateDoc(doc(db, USERS_COL, targetUserId), {
    role: newRole,
    atualizadoEm: serverTimestamp(),
  });
};

// ======== ATUALIZAR GESTOR DO USUÁRIO ========
export const assignGestor = async (targetUserId, gestorId) => {
  await updateDoc(doc(db, USERS_COL, targetUserId), {
    gestorId,
    atualizadoEm: serverTimestamp(),
  });
};

// ======== ATUALIZAR EQUIPE DO USUÁRIO ========
export const assignTeam = async (targetUserId, teamId) => {
  await updateDoc(doc(db, USERS_COL, targetUserId), {
    teamId,
    atualizadoEm: serverTimestamp(),
  });
};

// ======== DESATIVAR USUÁRIO ========
export const deactivateUser = async (targetUserId) => {
  await updateDoc(doc(db, USERS_COL, targetUserId), {
    status: 'inactive',
    atualizadoEm: serverTimestamp(),
  });
};

// ======== REATIVAR USUÁRIO ========
export const reactivateUser = async (targetUserId) => {
  await updateDoc(doc(db, USERS_COL, targetUserId), {
    status: 'active',
    atualizadoEm: serverTimestamp(),
  });
};

// ======== EXCLUIR USUÁRIO ========
export const deleteUser = async (targetUserId) => {
  await deleteDoc(doc(db, USERS_COL, targetUserId));
};

// ================================================================
// EQUIPES
// ================================================================

export const createTeam = async (profile, data) => {
  if (![ROLES.SUPER_ADMIN, ROLES.MASTER, ROLES.GESTOR].includes(profile.role)) {
    throw new Error('Sem permissão para criar equipes.');
  }

  const masterId = profile.role === ROLES.MASTER
    ? profile.uid
    : profile.role === ROLES.SUPER_ADMIN
      ? (data.masterId || null)
      : profile.masterId;

  const gestorId = profile.role === ROLES.GESTOR
    ? profile.uid
    : (data.gestorId || profile.uid);

  const teamRef = doc(collection(db, TEAMS_COL));
  await setDoc(teamRef, {
    nome: data.nome,
    gestorId,
    masterId,
    criadoEm: serverTimestamp(),
  });

  return teamRef.id;
};

export const subscribeTeams = (profile, callback) => {
  let q;

  if (profile.role === ROLES.SUPER_ADMIN) {
    q = query(collection(db, TEAMS_COL));
  } else if (profile.role === ROLES.MASTER) {
    q = query(collection(db, TEAMS_COL), where('masterId', '==', profile.uid));
  } else if (profile.role === ROLES.GESTOR) {
    q = query(collection(db, TEAMS_COL), where('gestorId', '==', profile.uid));
  } else {
    callback([]);
    return () => {};
  }

  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const updateTeam = async (teamId, data) => {
  await updateDoc(doc(db, TEAMS_COL, teamId), { ...data });
};

export const deleteTeam = async (teamId) => {
  const q = query(collection(db, USERS_COL), where('teamId', '==', teamId));
  const snap = await getDocs(q);
  const batch = writeBatch(db);
  snap.docs.forEach((d) => {
    batch.update(doc(db, USERS_COL, d.id), { teamId: null });
  });
  await batch.commit();
  await deleteDoc(doc(db, TEAMS_COL, teamId));
};

// ================================================================
// PERMISSÕES / FILTROS DE DADOS
// ================================================================

export const getDataFilter = (profile) => {
  switch (profile.role) {
    case ROLES.SUPER_ADMIN:
      // Super admin vê tudo da plataforma
      return { field: null, value: null, all: true };

    case ROLES.MASTER:
      return { field: 'masterId', value: profile.uid };

    case ROLES.GESTOR:
      return { field: 'gestorId', value: profile.uid, includeSelf: true };

    case ROLES.USUARIO:
      return { field: 'userId', value: profile.uid };

    default:
      return { field: 'userId', value: profile.uid };
  }
};

export const can = (profile, action) => {
  const permissions = {
    super_admin: [
      'manage_platform', 'manage_blog', 'manage_system_config',
      'manage_all_users', 'manage_plans', 'view_admin_dashboard',
      'manage_users', 'manage_teams', 'manage_gestores',
      'view_all_data', 'manage_whitelabel', 'manage_account',
      'create_simulation', 'create_client', 'export_pdf',
    ],
    master: [
      'manage_users', 'manage_teams', 'manage_gestores',
      'view_all_data', 'manage_whitelabel', 'manage_account',
      'create_simulation', 'create_client', 'export_pdf',
    ],
    gestor: [
      'manage_users', 'manage_teams',
      'view_team_data', 'create_simulation', 'create_client', 'export_pdf',
    ],
    usuario: [
      'view_own_data', 'create_simulation', 'create_client', 'export_pdf',
    ],
  };

  return permissions[profile.role]?.includes(action) || false;
};